import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { googleDriveService } from '@/services/googleDriveService';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshAuthState } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const errorParam = urlParams.get('error');
      const token = urlParams.get('token');
      const refreshToken = urlParams.get('refresh_token');
      const userStr = urlParams.get('user');

      // Debug logging
      console.log('OAuth Callback Debug:', {
        url: window.location.href,
        search: window.location.search,
        hasCode: code ? 'present' : 'missing',
        hasToken: token ? 'present' : 'missing',
        refreshToken: refreshToken ? 'present' : 'missing',
        userStr: userStr ? 'present' : 'missing',
        errorParam,
        state
      });

      if (errorParam) {
        setError('Authentication failed. Please try again.');
        setStatus('error');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      // Handle Google Drive setup callback
      if (state === 'google_drive_setup' && code) {
        try {
          // Exchange code for tokens using user credentials
          const redirectUri = `${window.location.origin}/auth/callback`;
          const tokens = await googleDriveService.exchangeCodeForTokens(code, redirectUri);

          // Store Google Drive configuration
          const credentials = googleDriveService.getUserCredentials();
          if (!credentials) {
            throw new Error('Google API credentials not found');
          }

          googleDriveService.saveConfig({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            expiresAt: new Date(Date.now() + tokens.expires_in * 1000),
            credentials
          });

          setStatus('success');
          // Redirect to settings with success message
          setTimeout(() => navigate('/settings?google_drive_connected=true'), 1500);
          return;
        } catch (error) {
          console.error('Google Drive setup failed:', error);
          setError('Google Drive setup failed');
          setStatus('error');
          setTimeout(() => navigate('/settings?google_drive_error=true'), 3000);
          return;
        }
      }

      // Case 1: Already processed tokens (direct from backend)
      if (token && refreshToken && userStr) {
        try {
          const user = JSON.parse(userStr);

          // Store auth data
          localStorage.setItem('godmode_auth_token', token);
          localStorage.setItem('godmode_auth', JSON.stringify({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            sessionExpiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString() // 15 minutes
          }));

          // Update auth store by refreshing state from localStorage
          refreshAuthState();

          // Close any open auth modals since authentication succeeded
          const { setShowAuthModal } = useAuthStore.getState();
          setShowAuthModal(false);

          setStatus('success');

          // Redirect to dashboard after a brief success message
          setTimeout(() => {
            // Close popup if this is in a popup window
            if (window.opener) {
              window.opener.postMessage({ type: 'oauth_success', user, token, refreshToken }, window.location.origin);
              window.close();
            } else {
              navigate('/');
            }
          }, 1500);

          return;
        } catch (err) {
          console.error('Failed to parse OAuth response:', err);
          setError('Invalid authentication response');
          setStatus('error');
          setTimeout(() => navigate('/'), 3000);
          return;
        }
      }

      // Case 2: OAuth code that needs to be exchanged
      if (code && state) {
        try {
          console.log('Exchanging OAuth code for tokens...');

          // Redirect to backend to complete OAuth flow
          // Note: The code from Google is already properly encoded, so we don't double-encode it
          const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
          const encodedState = encodeURIComponent(state);
          const backendUrl = `${window.location.origin}/api/auth/oauth-google?code=${code}&redirect_uri=${redirectUri}&state=${encodedState}`;

          console.log('Redirecting to backend:', {
            backendUrl: backendUrl.substring(0, 100) + '...',
            codeLength: code.length,
            codePreview: code.substring(0, 20) + '...'
          });
          window.location.href = backendUrl;
          return;

        } catch (err) {
          console.error('Failed to process OAuth code:', err);
          setError('Failed to complete authentication');
          setStatus('error');
          setTimeout(() => navigate('/'), 3000);
          return;
        }
      }

      // Case 3: No valid parameters
      setError('Missing authentication data');
      setStatus('error');
      setTimeout(() => navigate('/'), 3000);
    };

    handleOAuthCallback();
  }, [navigate, refreshAuthState]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full mx-auto p-6">
        <div className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <h2 className="text-xl font-semibold">Completing sign in...</h2>
              <p className="text-muted-foreground">Please wait while we finish setting up your account.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="rounded-full h-12 w-12 bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-green-600 dark:text-green-400">Sign in successful!</h2>
              <p className="text-muted-foreground">Redirecting you to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="rounded-full h-12 w-12 bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">Sign in failed</h2>
              <p className="text-muted-foreground">{error}</p>
              <p className="text-sm text-muted-foreground">Redirecting back to sign in page...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}